import { Anchor, Box, Button, Card, Container, Group, Stack, Table, Text, ThemeIcon, Title } from '@mantine/core'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuCircleCheck, LuFileText, LuTriangleAlert } from 'react-icons/lu'
import { CurrencyText } from '@/frontend/components/marina/CurrencyText'
import { DataTable } from '@/frontend/components/marina/DataTable'
import { MoneyText } from '@/frontend/components/marina/MoneyText'
import { PillNav } from '@/frontend/components/marina/PillNav'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import { useCanWrite } from '@/frontend/hooks/useAuth'
import { type QuoteStatus, useQuoteStatusCounts, useQuotes } from '@/frontend/hooks/useQuotes'

type TabValue = 'ALL' | QuoteStatus

const VALID: TabValue[] = ['ALL', 'DRAFT', 'SENT', 'ACCEPTED', 'REJECTED', 'EXPIRED', 'CONVERTED']

export const Route = createFileRoute('/dashboard/billing/quotes/')({
  validateSearch: (s: Record<string, unknown>): { tab?: TabValue } => ({
    tab: VALID.includes(s.tab as TabValue) ? (s.tab as TabValue) : undefined,
  }),
  component: QuotesList,
})

function QuotesList() {
  const { t } = useTranslation()
  const navigate = useNavigate({ from: Route.fullPath })
  const canWrite = useCanWrite()
  const { tab: rawTab } = Route.useSearch()
  const tab = rawTab ?? 'ALL'
  const setTab = (v: TabValue) => navigate({ search: () => ({ tab: v }), replace: true })
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 15

  const { data, isLoading } = useQuotes({
    q: query,
    status: tab === 'ALL' ? undefined : tab,
    page,
    pageSize,
  })
  const items = data?.items ?? []
  const total = data?.total ?? 0

  // Stat counts for alert banners + PillNav badges
  const { data: countsData } = useQuoteStatusCounts()
  const counts = countsData?.counts ?? {}
  const totalAll = Object.values(counts).reduce((s, n) => s + n, 0)
  const acceptedCount = counts.ACCEPTED ?? 0
  const expiredCount = counts.EXPIRED ?? 0

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="laut" size="lg" radius="md">
              <LuFileText size={18} />
            </ThemeIcon>
            <div>
              <Title order={2} ff="var(--font-display)" fw={600}>
                {t('page.billing.quotes.title')}
              </Title>
              <Text size="xs" c="dimmed">
                {t('page.billing.quotes.subtitle')}
              </Text>
            </div>
          </Group>
          <Group gap="sm" align="center">
            <input
              type="search"
              value={search}
              placeholder={t('page.billing.quotes.placeholder')}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setQuery(search)
                  setPage(1)
                }
              }}
              style={{
                fontSize: '0.8rem',
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid var(--mantine-color-default-border)',
                background: 'var(--app-card-bg)',
                color: 'inherit',
                width: 200,
                outline: 'none',
              }}
            />
            {canWrite && (
              <Button color="karang" size="sm" component={Link} to="/dashboard/billing/quotes/new">
                {t('page.billing.quotes.btn_new')}
              </Button>
            )}
          </Group>
        </Group>

        {/* Alert: penawaran diterima siap dikonversi */}
        {acceptedCount > 0 && tab !== 'ACCEPTED' && (
          <Box
            px="md"
            py="xs"
            style={{
              borderRadius: 10,
              background: 'light-dark(var(--mantine-color-teal-0), rgba(12,166,120,0.1))',
              cursor: 'pointer',
            }}
            onClick={() => {
              setTab('ACCEPTED')
              setPage(1)
            }}
          >
            <Group gap="xs" justify="space-between">
              <Group gap="xs">
                <ThemeIcon variant="light" color="teal" size="sm" radius="xl">
                  <LuCircleCheck size={13} />
                </ThemeIcon>
                <Text size="sm" fw={600} c="teal.7">
                  {t('page.billing.quotes.alert_accepted', { n: acceptedCount })}
                </Text>
              </Group>
              <Text size="xs" c="teal.6" fw={500}>
                {t('common.see_more')}
              </Text>
            </Group>
          </Box>
        )}

        {/* Alert: penawaran kadaluwarsa */}
        {expiredCount > 0 && tab !== 'EXPIRED' && (
          <Box
            px="md"
            py="xs"
            style={{
              borderRadius: 10,
              background: 'light-dark(var(--mantine-color-orange-0), rgba(200,120,40,0.1))',
              cursor: 'pointer',
            }}
            onClick={() => {
              setTab('EXPIRED')
              setPage(1)
            }}
          >
            <Group gap="xs" justify="space-between">
              <Group gap="xs">
                <ThemeIcon variant="light" color="orange" size="sm" radius="xl">
                  <LuTriangleAlert size={13} />
                </ThemeIcon>
                <Text size="sm" fw={600} c="orange.7">
                  {t('page.billing.quotes.alert_expired', { n: expiredCount })}
                </Text>
              </Group>
              <Text size="xs" c="orange.6" fw={500}>
                {t('common.see_more')}
              </Text>
            </Group>
          </Box>
        )}

        {/* PillNav */}
        <PillNav
          value={tab}
          onChange={(v) => {
            setTab(v)
            setPage(1)
          }}
          items={[
            { value: 'ALL', label: t('page.billing.quotes.tab_all'), count: totalAll || undefined, countColor: 'gray' },
            {
              value: 'DRAFT',
              label: t('page.billing.quotes.tab_draft'),
              count: counts.DRAFT || undefined,
              countColor: 'gray',
            },
            {
              value: 'SENT',
              label: t('page.billing.quotes.tab_sent'),
              count: counts.SENT || undefined,
              countColor: 'blue',
            },
            {
              value: 'ACCEPTED',
              label: t('page.billing.quotes.tab_accepted'),
              count: acceptedCount || undefined,
              countColor: 'teal',
            },
            {
              value: 'REJECTED',
              label: t('page.billing.quotes.tab_rejected'),
              count: counts.REJECTED || undefined,
              countColor: 'red',
            },
            {
              value: 'EXPIRED',
              label: t('page.billing.quotes.tab_expired'),
              count: expiredCount || undefined,
              countColor: 'orange',
            },
            {
              value: 'CONVERTED',
              label: t('page.billing.quotes.tab_converted'),
              count: counts.CONVERTED || undefined,
              countColor: 'laut',
            },
          ]}
        />

        {/* Table */}
        <Card radius="md" padding="lg">
          <DataTable
            isEmpty={!isLoading && items.length === 0}
            emptyLabel={
              tab === 'ACCEPTED'
                ? t('page.billing.quotes.empty_accepted')
                : tab === 'EXPIRED'
                  ? t('page.billing.quotes.empty_expired')
                  : t('page.billing.quotes.empty')
            }
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            columns={
              <>
                <Table.Th>{t('page.billing.quotes.col_code')}</Table.Th>
                <Table.Th>{t('page.billing.quotes.col_customer')}</Table.Th>
                <Table.Th ta="right">{t('page.billing.quotes.col_total')}</Table.Th>
                <Table.Th>{t('page.billing.quotes.col_valid')}</Table.Th>
                <Table.Th>{t('page.billing.quotes.col_status')}</Table.Th>
                <Table.Th ta="right">{t('page.billing.quotes.col_actions')}</Table.Th>
              </>
            }
          >
            {items.map((q) => {
              const customerName = q.customer ? `${q.customer.firstName} ${q.customer.lastName}`.trim() : '—'

              const daysLeft = q.validUntil ? dayjs(q.validUntil).diff(dayjs(), 'day') : null
              const isExpiringSoon = q.status === 'SENT' && daysLeft !== null && daysLeft >= 0 && daysLeft <= 7
              const isOverdue = q.status === 'SENT' && daysLeft !== null && daysLeft < 0

              const rowBg =
                q.status === 'ACCEPTED'
                  ? 'light-dark(var(--mantine-color-teal-0), rgba(12,166,120,0.07))'
                  : q.status === 'EXPIRED'
                    ? 'light-dark(var(--mantine-color-orange-0), rgba(200,120,40,0.07))'
                    : undefined

              return (
                <Table.Tr
                  key={q.id}
                  style={{ cursor: 'pointer', background: rowBg }}
                  onClick={() => navigate({ to: `/dashboard/billing/quotes/${q.id}` as any })}
                >
                  {/* Kode + tanggal terbit */}
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {q.code}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {dayjs(q.issueDate).format('DD MMM YYYY')}
                    </Text>
                  </Table.Td>

                  {/* Pelanggan + kapal */}
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {customerName}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {q.boat?.name ? q.boat.name : (q.customer?.email ?? '—')}
                    </Text>
                  </Table.Td>

                  {/* Total */}
                  <Table.Td ta="right">
                    <MoneyText amount={q.totalIdr} currency={q.currency} fw={600} size="sm" />
                  </Table.Td>

                  {/* Berlaku sampai + urgency */}
                  <Table.Td>
                    {q.validUntil ? (
                      <>
                        <Text
                          size="sm"
                          c={isOverdue ? 'red.6' : isExpiringSoon ? 'orange.6' : undefined}
                          fw={isExpiringSoon || isOverdue ? 500 : undefined}
                        >
                          {dayjs(q.validUntil).format('DD MMM YYYY')}
                        </Text>
                        {(isExpiringSoon || isOverdue) && (
                          <Text size="xs" c={isOverdue ? 'red.5' : 'orange.5'}>
                            {isOverdue
                              ? t('page.billing.quotes.days_ago', { n: Math.abs(daysLeft!) })
                              : daysLeft === 0
                                ? t('page.billing.quotes.today')
                                : t('page.billing.quotes.days_left', { n: daysLeft })}
                          </Text>
                        )}
                      </>
                    ) : (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>

                  {/* Status */}
                  <Table.Td>
                    <StatusBadge status={q.status} kind="quote" />
                  </Table.Td>

                  {/* Aksi */}
                  <Table.Td ta="right" onClick={(e) => e.stopPropagation()}>
                    <Stack gap={2} align="flex-end">
                      <Anchor
                        component={Link}
                        to={`/dashboard/billing/quotes/${q.id}` as any}
                        c="laut"
                        size="sm"
                        fw={500}
                      >
                        {t('common.see_more')}
                      </Anchor>
                      {q.invoice && (
                        <Anchor
                          component={Link}
                          to={`/dashboard/billing/invoices/${q.invoice.id}` as any}
                          c="dimmed"
                          size="xs"
                        >
                          {q.invoice.code}
                        </Anchor>
                      )}
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </DataTable>
        </Card>
      </Stack>
    </Container>
  )
}
